import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-charity-crm',
  version: '1.0.0',
  properties: [
    // Credential selection
    {
      key: 'credentialId',
      label: 'Access Charity CRM Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'access-charity-crm',
      },
      description: 'Select your Access Charity CRM (thankQ) credential',
    },

    // Resource selection
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'actions',
      options: [
        { value: 'actions', name: 'Actions' },
        { value: 'communications', name: 'Communications' },
        { value: 'contactAddresses', name: 'Contact Addresses' },
        { value: 'attachments', name: 'Attachments' },
        { value: 'utility', name: 'Utility' },
      ],
      description: 'The CRM resource to interact with',
    },

    // ===== ACTIONS RESOURCE =====
    {
      key: 'action',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'listActions',
      options: [
        { value: 'listActions', name: 'List Actions' },
        { value: 'getAction', name: 'Get Action' },
        { value: 'createAction', name: 'Create Action' },
        { value: 'updateAction', name: 'Update Action' },
      ],
      displayOptions: {
        show: {
          resource: ['actions'],
        },
      },
    },

    // ===== COMMUNICATIONS RESOURCE =====
    {
      key: 'action',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'listCommunications',
      options: [
        { value: 'listCommunications', name: 'List Communications' },
        { value: 'getCommunication', name: 'Get Communication' },
        { value: 'createCommunication', name: 'Create Communication' },
        { value: 'updateCommunication', name: 'Update Communication' },
      ],
      displayOptions: {
        show: {
          resource: ['communications'],
        },
      },
    },

    // ===== CONTACT ADDRESSES RESOURCE =====
    {
      key: 'action',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'listContactAddresses',
      options: [
        { value: 'listContactAddresses', name: 'List Contact Addresses' },
        { value: 'getContactAddress', name: 'Get Contact Address' },
        { value: 'createContactAddress', name: 'Create Contact Address' },
        { value: 'deleteContactAddress', name: 'Delete Contact Address' },
      ],
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
        },
      },
    },

    // ===== ATTACHMENTS RESOURCE =====
    {
      key: 'action',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'listAttachments',
      options: [
        { value: 'listAttachments', name: 'List Attachments' },
        { value: 'getAttachment', name: 'Get Attachment' },
        { value: 'createAttachment', name: 'Create Attachment' },
        { value: 'deleteAttachment', name: 'Delete Attachment' },
      ],
      displayOptions: {
        show: {
          resource: ['attachments'],
        },
      },
    },

    // ===== UTILITY RESOURCE =====
    {
      key: 'action',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'healthCheck',
      options: [
        { value: 'healthCheck', name: 'Health Check' },
      ],
      displayOptions: {
        show: {
          resource: ['utility'],
        },
      },
    },

    // ===== PAGINATION (List operations) =====
    {
      key: 'page',
      label: 'Page',
      type: 'number',
      required: false,
      default: 1,
      placeholder: '1',
      description: 'Page number for pagination',
      displayOptions: {
        show: {
          action: ['listActions', 'listCommunications', 'listContactAddresses', 'listAttachments'],
        },
      },
    },
    {
      key: 'pageSize',
      label: 'Page Size',
      type: 'number',
      required: false,
      default: 50,
      placeholder: '50',
      description: 'Results per page (max 1000)',
      displayOptions: {
        show: {
          action: ['listActions', 'listCommunications', 'listContactAddresses', 'listAttachments'],
        },
      },
    },

    // ===== ACTION-SPECIFIC FIELDS =====
    {
      key: 'actionRef',
      label: 'Action Reference',
      type: 'string',
      required: true,
      placeholder: 'e.g., ACT123456',
      description: 'The unique reference ID of the action',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['getAction', 'updateAction'],
        },
      },
    },
    {
      key: 'subject',
      label: 'Subject (Contact Reference)',
      type: 'string',
      required: false,
      placeholder: 'e.g., CONTACT123',
      description: 'Contact reference - filter for list or required for create',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['listActions', 'createAction'],
        },
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'string',
      required: false,
      placeholder: 'e.g., Open, Completed',
      description: 'Filter by or set action status',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['listActions', 'createAction', 'updateAction'],
        },
      },
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'string',
      required: false,
      placeholder: 'e.g., High, Normal, Low',
      description: 'Filter by or set action priority',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['listActions', 'createAction', 'updateAction'],
        },
      },
    },
    {
      key: 'category',
      label: 'Category',
      type: 'string',
      required: false,
      placeholder: 'e.g., Follow-up, Call',
      description: 'Filter by or set action category',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['listActions', 'createAction'],
        },
      },
    },
    {
      key: 'actionOnUser',
      label: 'Assigned User',
      type: 'string',
      required: false,
      placeholder: 'e.g., user@example.com',
      description: 'User responsible for the action',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['createAction'],
        },
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      type: 'string',
      required: false,
      placeholder: 'e.g., 2024-12-31T23:59:59Z',
      description: 'Due date in ISO 8601 format',
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['createAction', 'updateAction'],
        },
      },
    },
    {
      key: 'notes',
      label: 'Notes',
      type: 'string',
      required: false,
      placeholder: 'Enter action notes...',
      description: 'Notes or description for the action',
      typeOptions: {
        rows: 3,
      },
      displayOptions: {
        show: {
          resource: ['actions'],
          action: ['createAction', 'updateAction'],
        },
      },
    },

    // ===== COMMUNICATION-SPECIFIC FIELDS =====
    {
      key: 'communicationId',
      label: 'Communication ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., COM123456',
      description: 'The unique ID of the communication',
      displayOptions: {
        show: {
          resource: ['communications'],
          action: ['getCommunication', 'updateCommunication'],
        },
      },
    },
    {
      key: 'contactRef',
      label: 'Contact Reference',
      type: 'string',
      required: false,
      placeholder: 'e.g., CONTACT123',
      description: 'Contact reference for filtering or creating',
      displayOptions: {
        show: {
          resource: ['communications'],
          action: ['listCommunications', 'createCommunication'],
        },
      },
    },
    {
      key: 'communicationType',
      label: 'Type',
      type: 'string',
      required: false,
      placeholder: 'e.g., Email, Phone, Letter',
      description: 'Communication type filter or value',
      displayOptions: {
        show: {
          resource: ['communications'],
          action: ['listCommunications', 'createCommunication'],
        },
      },
    },
    {
      key: 'communicationSubject',
      label: 'Subject',
      type: 'string',
      required: false,
      placeholder: 'Enter subject...',
      description: 'Subject/title of the communication',
      displayOptions: {
        show: {
          resource: ['communications'],
          action: ['createCommunication', 'updateCommunication'],
        },
      },
    },
    {
      key: 'communicationBody',
      label: 'Body',
      type: 'string',
      required: false,
      placeholder: 'Enter communication body...',
      description: 'Body/content of the communication',
      typeOptions: {
        rows: 4,
      },
      displayOptions: {
        show: {
          resource: ['communications'],
          action: ['createCommunication', 'updateCommunication'],
        },
      },
    },

    // ===== CONTACT ADDRESS-SPECIFIC FIELDS =====
    {
      key: 'contactAddressId',
      label: 'Contact Address ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., ADDR123456',
      description: 'The unique ID of the contact address',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['getContactAddress', 'deleteContactAddress'],
        },
      },
    },
    {
      key: 'addressContactRef',
      label: 'Contact Reference',
      type: 'string',
      required: false,
      placeholder: 'e.g., CONTACT123',
      description: 'Contact reference for filtering or creating address',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['listContactAddresses', 'createContactAddress'],
        },
      },
    },
    {
      key: 'addressLine1',
      label: 'Address Line 1',
      type: 'string',
      required: false,
      placeholder: 'e.g., 123 Main Street',
      description: 'First line of the address',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['createContactAddress'],
        },
      },
    },
    {
      key: 'addressLine2',
      label: 'Address Line 2',
      type: 'string',
      required: false,
      placeholder: 'e.g., Suite 100',
      description: 'Second line of the address',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['createContactAddress'],
        },
      },
    },
    {
      key: 'city',
      label: 'City',
      type: 'string',
      required: false,
      placeholder: 'e.g., London',
      description: 'City',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['createContactAddress'],
        },
      },
    },
    {
      key: 'postcode',
      label: 'Postcode',
      type: 'string',
      required: false,
      placeholder: 'e.g., SW1A 1AA',
      description: 'Postal code',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['createContactAddress'],
        },
      },
    },
    {
      key: 'country',
      label: 'Country',
      type: 'string',
      required: false,
      placeholder: 'e.g., United Kingdom',
      description: 'Country',
      displayOptions: {
        show: {
          resource: ['contactAddresses'],
          action: ['createContactAddress'],
        },
      },
    },

    // ===== ATTACHMENT-SPECIFIC FIELDS =====
    {
      key: 'attachmentRef',
      label: 'Attachment Reference',
      type: 'string',
      required: true,
      placeholder: 'e.g., ATT123456',
      description: 'The unique reference of the attachment',
      displayOptions: {
        show: {
          resource: ['attachments'],
          action: ['getAttachment', 'deleteAttachment'],
        },
      },
    },
    {
      key: 'fileName',
      label: 'File Name',
      type: 'string',
      required: false,
      placeholder: 'e.g., document.pdf',
      description: 'Name of the file to upload',
      displayOptions: {
        show: {
          resource: ['attachments'],
          action: ['createAttachment'],
        },
      },
    },
    {
      key: 'fileContent',
      label: 'File Content (Base64)',
      type: 'string',
      required: false,
      placeholder: 'Base64 encoded file content or {{ $node.prev.output.fileData }}',
      description: 'Base64 encoded file content - can reference previous node output',
      typeOptions: {
        rows: 3,
      },
      displayOptions: {
        show: {
          resource: ['attachments'],
          action: ['createAttachment'],
        },
      },
    },
    {
      key: 'relatedEntity',
      label: 'Related Entity',
      type: 'string',
      required: false,
      placeholder: 'e.g., Contact, Action',
      description: 'Entity type to attach the file to',
      displayOptions: {
        show: {
          resource: ['attachments'],
          action: ['createAttachment'],
        },
      },
    },
    {
      key: 'relatedEntityRef',
      label: 'Related Entity Reference',
      type: 'string',
      required: false,
      placeholder: 'e.g., CONTACT123',
      description: 'Reference of the entity to attach to',
      displayOptions: {
        show: {
          resource: ['attachments'],
          action: ['createAttachment'],
        },
      },
    },

    // ===== ADVANCED: Full body JSON =====
    {
      key: 'body',
      label: 'Request Body (JSON)',
      type: 'json',
      required: false,
      placeholder: '{\n  "key": "value"\n}',
      description: 'Full request body for create/update operations (overrides individual fields)',
      displayOptions: {
        show: {
          action: ['createAction', 'updateAction', 'createCommunication', 'updateCommunication', 'createContactAddress', 'createAttachment'],
        },
      },
    },
  ],
};

export default schema;
